/**
 * 商品多列网格 + 触底加载。IntersectionObserver 哨兵驱动 onLoadMore;
 * 底部翻页态:错误→点击重试 / 加载中→spinner / 到底→「— 没有更多了 —」(五态·部分)。
 */
import { useEffect, useRef } from 'react';
import type { ProductFeedItem } from '@/api/guide';
import { ProductCard } from './ProductCard';

interface ProductGridProps {
  items: ProductFeedItem[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** 翻页(非首屏)错误:底部展示重试而非整页错误。 */
  isPageError?: boolean;
  /** 到底是否展示「没有更多了」(纯前端过滤场景可关)。 */
  showNoMore?: boolean;
  onLoadMore?: () => void;
}

export function ProductGrid({
  items,
  hasNextPage,
  isFetchingNextPage,
  isPageError,
  showNoMore = true,
  onLoadMore,
}: ProductGridProps) {
  const sentinel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isPageError || !onLoadMore) return;
    const el = sentinel.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      { rootMargin: '320px' },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [hasNextPage, isFetchingNextPage, isPageError, onLoadMore, items.length]);

  return (
    <>
      <section className="grid-cards">
        {items.map((it, i) => (
          <ProductCard key={it.productId ?? `idx-${i}`} item={it} />
        ))}
      </section>
      <div ref={sentinel} aria-hidden="true" style={{ height: 1 }} />
      <div className="row" style={{ justifyContent: 'center', padding: 'var(--space-8) 0' }}>
        {isPageError ? (
          <button type="button" className="btn btn-secondary" onClick={onLoadMore}>
            网络异常,点击重试
          </button>
        ) : isFetchingNextPage ? (
          <span className="muted sm">加载中…</span>
        ) : !hasNextPage && showNoMore && items.length > 0 ? (
          <span className="muted sm">— 没有更多了 —</span>
        ) : null}
      </div>
    </>
  );
}
